import { useCallback, useEffect, useState } from "react";
import { useWallet } from "../wallet/WalletContext";
import { adminAccounts, contracts } from "../config";
import {
  ORDERBOOK_BUILD_HASH, MAX_FEE_BPS, addMarket, adminCancelMany, deleteMarket, editMarket, forceClose, fromUnits,
  getAccount, getBook, getExchangeConfig, getOrderbookCodeHash, hasEosioCode, hasExchangeAbi, installOrderbook, listMarkets,
  lookupToken, setFee, setFeeAccount, setMaxFills, setPaused, symCode, symPrec, toUnits,
  type ExchangeConfig, type Market, type Order,
} from "../api/exchangeApi";
import { confirmAction, confirmDanger, showError, showSuccess, showWarning } from "./Dialog";

// Presets for the tokens this was built for. HIPS's issuing contract wasn't
// known when this was written — type it in when adding the market.
const PRESETS = [
  { label: "TWAX (StockWax)", contract: "stockwaxcoms", symbol: "TWAX", website: "https://stockwax.com" },
  { label: "HIPS (Austin's Finest)", contract: "", symbol: "HIPS", website: "" },
];

interface MarketStats { bids: Order[]; asks: Order[] }

export function ExchangeAdminPanel() {
  const { session, account } = useWallet();
  const [hash, setHash] = useState<string | null | undefined>(undefined);
  const [codeLinked, setCodeLinked] = useState<boolean | null>(null);
  const [cfg, setCfg] = useState<ExchangeConfig | null>(null);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [stats, setStats] = useState<Record<number, MarketStats>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [stepMsg, setStepMsg] = useState("");
  const [abiOk, setAbiOk] = useState<boolean | null>(null);

  // install/init
  const [initAdmin, setInitAdmin] = useState(adminAccounts[0] ?? "");
  const [initFee, setInitFee] = useState(adminAccounts[0] ?? "");
  // settings
  const [feePct, setFeePct] = useState("0.20");
  const [feeAcct, setFeeAcct] = useState("");
  const [maxFills, setMaxFillsInput] = useState("25");

  const [chainErr, setChainErr] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    setChainErr(null);
    // Each read updates the page on its own, so one slow call can't hold up the rest.
    getOrderbookCodeHash()
      .then(setHash)
      .catch((e) => { setHash(null); setChainErr(e instanceof Error ? e.message : String(e)); });
    hasExchangeAbi().then(setAbiOk).catch(() => setAbiOk(null));
    getAccount(contracts.orderbook).then((a) => setCodeLinked(a ? hasEosioCode(a) : null)).catch(() => setCodeLinked(null));
    const [c, ms] = await Promise.all([
      getExchangeConfig().catch(() => null),
      listMarkets().catch(() => [] as Market[]),
    ]);
    setCfg(c);
    setMarkets(ms);
    if (c) {
      setFeePct((c.taker_fee_bps / 100).toFixed(2));
      setFeeAcct(c.fee_account);
      setMaxFillsInput(String(c.max_fills));
    }
    const st: Record<number, MarketStats> = {};
    await Promise.all(ms.map(async (m) => {
      const [bids, asks] = await Promise.all([getBook(m.id, "bids", 500).catch(() => []), getBook(m.id, "asks", 500).catch(() => [])]);
      st[m.id] = { bids, asks };
    }));
    setStats(st);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function run(label: string, fn: () => Promise<unknown>, success: string) {
    if (!session) return;
    setBusy(label);
    try {
      await fn();
      showSuccess(success);
      [1500, 5000].forEach((ms) => setTimeout(refresh, ms));
    } catch (e) {
      showError(e, `Couldn't ${label}`);
    } finally {
      setBusy(null);
    }
  }

  const accountExists = typeof hash === "string" && hash !== "missing";
  const deployed = accountExists && /[^0]/.test(hash!);
  const upToDate = hash === ORDERBOOK_BUILD_HASH;
  const isContractAcct = account === contracts.orderbook;
  const isExAdmin = !!cfg && (account === cfg.admin || account === contracts.orderbook);

  return (
    <div className="admin-section">
      <h2>Token exchange (order books)</h2>
      <p className="admin-section__subtitle">
        On-chain limit-order books pairing game tokens with WAX, run by the <code>{contracts.orderbook}</code>{" "}
        contract. Traders send tokens straight to the contract; the site never holds funds. Settings below are
        wallet-signed by the exchange admin and enforced on chain.
      </p>

      {/* ---------------- 1. contract */}
      <h3 className="ex-admin__h">1 · Contract</h3>
      <div className="ex-admin__status">
        <div>Account <code>{contracts.orderbook}</code>: <strong>{hash === undefined ? "checking…" : hash === null ? "couldn't reach the WAX chain" : !accountExists ? "not found — create it first" : !deployed ? "no contract yet" : upToDate ? "installed ✓ (current build)" : `different build (${hash!.slice(0, 12)}…)`}</strong></div>
        <div>Payout permission (eosio.code): <strong>{codeLinked === null ? "—" : codeLinked ? "linked ✓" : "not linked"}</strong></div>
        <div>Initialised: <strong>{cfg ? `yes — admin ${cfg.admin}, fees to ${cfg.fee_account}` : "no"}</strong></div>
      </div>
      {chainErr && (
        <div className="callout callout--warning">
          Couldn't reach the WAX chain ({chainErr}).{" "}
          <button className="btn btn--small btn--outline" onClick={() => { setHash(undefined); refresh(); }}>Retry</button>
        </div>
      )}
      {hash === "missing" && (
        <div className="callout callout--warning">
          Create a WAX account named <code>{contracts.orderbook}</code> (or set <code>VITE_ORDERBOOK_CONTRACT</code> to one
          you own) with at least ~1 MB of RAM. It must be a dedicated account — installing replaces any contract on it.
        </div>
      )}
      {accountExists && (!upToDate || !codeLinked || !cfg || abiOk === false) && (
        isContractAcct ? (
          <div className="ex-admin__form">
            {!cfg && (
              <>
                <label>Exchange admin account<input value={initAdmin} onChange={(e) => setInitAdmin(e.target.value.trim())} /></label>
                <label>Fee account (receives taker fees)<input value={initFee} onChange={(e) => setInitFee(e.target.value.trim())} /></label>
              </>
            )}
            <button
              className="btn btn--primary"
              disabled={!!busy}
              onClick={() => run("install the exchange", () => installOrderbook(session!, cfg ? undefined : { admin: initAdmin, fee_account: initFee }, setStepMsg).finally(() => setStepMsg("")), "Exchange contract installed.")}
            >
              {busy === "install the exchange" ? "Waiting for wallet…" : !upToDate ? (cfg ? "Install update" : "Install & initialise") : (abiOk === false || !codeLinked) ? "Continue install" : "Initialise"}
            </button>
            {stepMsg && <div className="state-message">{stepMsg}</div>}
            <p className="ex-fineprint">Checks the contract file's sha256 ({ORDERBOOK_BUILD_HASH.slice(0, 12)}…) before asking your wallet to sign.
              {" "}Your wallet asks up to three times (code, interface + payout permission, initialise). If one step fails, click again — finished steps are skipped.</p>
          </div>
        ) : (
          <div className="callout callout--warning">Connect as <code>{contracts.orderbook}</code> to install / initialise.</div>
        )
      )}

      {cfg && (
        <>
          {/* ---------------- 2. settings */}
          <h3 className="ex-admin__h">2 · Fees & trading</h3>
          {!isExAdmin && <div className="callout callout--warning">Connect as <code>{cfg.admin}</code> (the exchange admin) to change these.</div>}
          <div className="ex-admin__grid">
            <label>
              Taker fee (%) — max {(MAX_FEE_BPS / 100).toFixed(2)}%
              <div className="ex-admin__inline">
                <input value={feePct} onChange={(e) => setFeePct(e.target.value)} inputMode="decimal" />
                <button className="btn btn--small btn--primary" disabled={!isExAdmin || !!busy} onClick={() => {
                  const bps = Math.round(Number(feePct) * 100);
                  if (!Number.isFinite(bps) || bps < 0 || bps > MAX_FEE_BPS || !/^\d*(\.\d{0,2})?$/.test(feePct.trim()))
                    return showWarning(`Enter a fee between 0 and ${(MAX_FEE_BPS / 100).toFixed(2)} with at most 2 decimals.`);
                  run("set the fee", () => setFee(session!, bps), `Taker fee is now ${(bps / 100).toFixed(2)}%.`);
                }}>Save</button>
              </div>
              <span className="ex-fineprint">Now {(cfg.taker_fee_bps / 100).toFixed(2)}% ({cfg.taker_fee_bps} bps), paid in WAX by whoever fills a resting order.</span>
            </label>
            <label>
              Fee account
              <div className="ex-admin__inline">
                <input value={feeAcct} onChange={(e) => setFeeAcct(e.target.value.trim())} />
                <button className="btn btn--small btn--primary" disabled={!isExAdmin || !!busy} onClick={() => run("set the fee account", () => setFeeAccount(session!, feeAcct), `Fees now go to ${feeAcct}.`)}>Save</button>
              </div>
            </label>
            <label>
              Max fills per order (CPU guard)
              <div className="ex-admin__inline">
                <input value={maxFills} onChange={(e) => setMaxFillsInput(e.target.value)} inputMode="numeric" />
                <button className="btn btn--small btn--primary" disabled={!isExAdmin || !!busy} onClick={() => run("set max fills", () => setMaxFills(session!, Number(maxFills)), "Saved.")}>Save</button>
              </div>
            </label>
            <div className="ex-admin__toggle">
              <span>Trading is <strong>{cfg.paused ? "PAUSED" : "live"}</strong></span>
              <button className={`btn btn--small ${cfg.paused ? "btn--primary" : "btn--danger"}`} disabled={!isExAdmin || !!busy}
                onClick={() => run(cfg.paused ? "resume trading" : "pause trading", () => setPaused(session!, !cfg.paused), cfg.paused ? "Trading resumed." : "Trading paused. Cancels still work.")}>
                {cfg.paused ? "Resume trading" : "Pause all trading"}
              </button>
            </div>
          </div>

          {/* ---------------- 3. add */}
          <h3 className="ex-admin__h">3 · Add a token market</h3>
          <AddMarketForm disabled={!isExAdmin || !!busy} onAdd={(input) => run("add the market", () => addMarket(session!, input), `${input.title} market is live.`)} />

          {/* ---------------- 4. markets */}
          <h3 className="ex-admin__h">4 · Markets ({markets.length})</h3>
          {markets.length === 0 ? (
            <div className="state-message">No markets yet.</div>
          ) : (
            <div className="ex-admin__markets">
              {markets.map((m) => (
                <MarketAdminRow key={m.id} m={m} st={stats[m.id]} canEdit={isExAdmin && !busy} run={run} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ add form

function AddMarketForm({ disabled, onAdd }: { disabled: boolean; onAdd: (m: Parameters<typeof addMarket>[1]) => void }) {
  const [contract, setContract] = useState("");
  const [symbol, setSymbol] = useState("");
  const [quoteContract, setQuoteContract] = useState("eosio.token");
  const [quoteSymbol, setQuoteSymbol] = useState("WAX");
  const [minOrder, setMinOrder] = useState("1");
  const [title, setTitle] = useState("");
  const [logo, setLogo] = useState("");
  const [website, setWebsite] = useState("");
  const [found, setFound] = useState<{ sym: string; supply: string; issuer: string } | null | undefined>(undefined);
  const [quoteFound, setQuoteFound] = useState<{ sym: string } | null | undefined>(undefined);

  useEffect(() => {
    if (!contract || !symbol) return setFound(undefined);
    const t = setTimeout(() => lookupToken(contract, symbol).then(setFound).catch(() => setFound(null)), 400);
    return () => clearTimeout(t);
  }, [contract, symbol]);
  useEffect(() => {
    if (!quoteContract || !quoteSymbol) return setQuoteFound(undefined);
    const t = setTimeout(() => lookupToken(quoteContract, quoteSymbol).then(setQuoteFound).catch(() => setQuoteFound(null)), 400);
    return () => clearTimeout(t);
  }, [quoteContract, quoteSymbol]);

  function submit() {
    if (!found || !quoteFound) return showWarning("Both tokens must be found on chain first.");
    const qp = Number(quoteFound.sym.split(",")[0]);
    let minU: bigint;
    try { minU = toUnits(minOrder, qp); } catch (e) { return showWarning(e instanceof Error ? e.message : String(e)); }
    if (minU <= 0n) return showWarning("Minimum order must be above zero.");
    onAdd({
      base: { sym: found.sym, contract },
      quote: { sym: quoteFound.sym, contract: quoteContract },
      min_quote: minU.toString(),
      title: title || `${symbol.toUpperCase()} / ${quoteSymbol.toUpperCase()}`,
      logo,
      website,
    });
  }

  return (
    <div className="ex-admin__form">
      <div className="ex-admin__presets">
        Quick fill:{" "}
        {PRESETS.map((p) => (
          <button key={p.label} className="btn btn--small btn--outline" onClick={() => {
            setContract(p.contract); setSymbol(p.symbol); setWebsite(p.website); setTitle(`${p.symbol} / WAX`);
          }}>{p.label}</button>
        ))}
      </div>
      <div className="ex-admin__grid">
        <label>Token contract<input value={contract} onChange={(e) => setContract(e.target.value.trim().toLowerCase())} placeholder="e.g. stockwaxcoms" /></label>
        <label>Token symbol<input value={symbol} onChange={(e) => setSymbol(e.target.value.trim().toUpperCase())} placeholder="e.g. TWAX" /></label>
        <label>Paired with (contract)<input value={quoteContract} onChange={(e) => setQuoteContract(e.target.value.trim().toLowerCase())} /></label>
        <label>Paired with (symbol)<input value={quoteSymbol} onChange={(e) => setQuoteSymbol(e.target.value.trim().toUpperCase())} /></label>
        <label>Minimum order ({quoteSymbol || "quote"})<input value={minOrder} onChange={(e) => setMinOrder(e.target.value)} inputMode="decimal" /></label>
        <label>Display name<input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`${symbol || "TOKEN"} / ${quoteSymbol || "WAX"}`} maxLength={64} /></label>
        <label>Logo (image URL or IPFS hash)<input value={logo} onChange={(e) => setLogo(e.target.value.trim())} maxLength={256} /></label>
        <label>Website / game link<input value={website} onChange={(e) => setWebsite(e.target.value.trim())} maxLength={256} /></label>
      </div>
      <div className="ex-fineprint">
        {found === undefined ? "Enter a token contract and symbol to look it up." :
          found === null ? <span className="ex-down">Token not found on that contract.</span> :
          <>Found <strong>{found.sym.split(",")[1]}</strong> — {found.sym.split(",")[0]} decimals, supply {found.supply}, issuer {found.issuer}.</>}
        {quoteFound === null && <span className="ex-down"> Quote token not found.</span>}
      </div>
      <button className="btn btn--primary" disabled={disabled || !found || !quoteFound} onClick={submit}>Add market</button>
    </div>
  );
}

// ------------------------------------------------------------------ market row

function MarketAdminRow({ m, st, canEdit, run }: {
  m: Market;
  st?: MarketStats;
  canEdit: boolean;
  run: (label: string, fn: () => Promise<unknown>, success: string) => Promise<void>;
}) {
  const { session } = useWallet();
  const qp = symPrec(m.quote), bp = symPrec(m.base), Q = symCode(m.quote), B = symCode(m.base);
  const [editing, setEditing] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [title, setTitle] = useState(m.title);
  const [logo, setLogo] = useState(m.logo);
  const [website, setWebsite] = useState(m.website);
  const [minOrder, setMinOrder] = useState(fromUnits(m.min_quote, qp));
  const nOpen = (st?.bids.length ?? 0) + (st?.asks.length ?? 0);
  const escrowQ = st?.bids.reduce((s, o) => s + BigInt(o.remaining), 0n) ?? 0n;
  const escrowB = st?.asks.reduce((s, o) => s + BigInt(o.remaining), 0n) ?? 0n;

  const fields = () => ({ enabled: m.enabled, min_quote: m.min_quote, title: m.title, logo: m.logo, website: m.website });

  async function refundAll() {
    if (!st || !session) return;
    const all = [...st.bids.map((o) => ({ isBid: true, id: o.id })), ...st.asks.map((o) => ({ isBid: false, id: o.id }))];
    if (!(await confirmDanger(`Refund all ${all.length} open orders on ${m.title} to their owners? (Sent in batches of 20.)`, "Refund all"))) return;
    await run("refund open orders", async () => {
      for (let i = 0; i < all.length; i += 20) await adminCancelMany(session, m.id, all.slice(i, i + 20));
    }, "All open orders refunded.");
  }

  return (
    <div className={`ex-admin__market ${m.enabled ? "" : "ex-admin__market--off"}`}>
      <div className="ex-admin__market-head">
        <div>
          <strong>#{m.id} {m.title}</strong> <span className="ex-fineprint">{B}@{m.base.contract} / {Q}@{m.quote.contract}</span>
          <div className="ex-fineprint">
            {m.enabled ? "Enabled" : "Disabled"} · min {fromUnits(m.min_quote, qp)} {Q} · last {BigInt(m.last_price) > 0n ? fromUnits(m.last_price, qp) : "—"} ·
            vol {fromUnits(m.volume_quote, qp)} {Q} · {String(m.trade_count)} trades · {nOpen} open orders
            {nOpen > 0 && <> (escrow {fromUnits(escrowQ, qp)} {Q} + {fromUnits(escrowB, bp)} {B})</>}
          </div>
        </div>
        <div className="ex-admin__actions">
          <a className="btn btn--small btn--outline" href={`/exchange/${m.id}`}>View</a>
          <button className="btn btn--small btn--outline" disabled={!canEdit} onClick={() => setEditing(!editing)}>Edit</button>
          <button className={`btn btn--small ${m.enabled ? "btn--danger" : "btn--primary"}`} disabled={!canEdit}
            onClick={() => run(m.enabled ? "disable the market" : "enable the market", () => editMarket(session!, m.id, { ...fields(), enabled: !m.enabled }), m.enabled ? "Market disabled (cancels still work)." : "Market enabled.")}>
            {m.enabled ? "Disable" : "Enable"}
          </button>
          <button className="btn btn--small btn--outline" onClick={() => setShowOrders(!showOrders)}>Orders</button>
          {nOpen > 0 ? (
            <button className="btn btn--small btn--danger" disabled={!canEdit} onClick={refundAll}>Refund all</button>
          ) : (
            <button className="btn btn--small btn--danger" disabled={!canEdit} onClick={async () => {
              if (await confirmDanger(`Delete market ${m.title}? Its trade history is removed too.`, "Delete market"))
                run("delete the market", () => deleteMarket(session!, m.id), "Market deleted.");
            }}>Delete</button>
          )}
        </div>
      </div>

      {editing && (
        <div className="ex-admin__grid">
          <label>Display name<input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={64} /></label>
          <label>Minimum order ({Q})<input value={minOrder} onChange={(e) => setMinOrder(e.target.value)} /></label>
          <label>Logo<input value={logo} onChange={(e) => setLogo(e.target.value.trim())} maxLength={256} /></label>
          <label>Website<input value={website} onChange={(e) => setWebsite(e.target.value.trim())} maxLength={256} /></label>
          <button className="btn btn--primary" disabled={!canEdit} onClick={() => {
            let minU: bigint;
            try { minU = toUnits(minOrder, qp); } catch (e) { return showWarning(e instanceof Error ? e.message : String(e)); }
            run("save the market", () => editMarket(session!, m.id, { enabled: m.enabled, min_quote: minU.toString(), title, logo, website }), "Market updated.")
              .then(() => setEditing(false));
          }}>Save changes</button>
        </div>
      )}

      {showOrders && st && (
        nOpen === 0 ? <div className="state-message">No open orders.</div> : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Side</th><th>Owner</th><th>Price ({Q})</th><th>Remaining</th><th /></tr></thead>
              <tbody>
                {[...st.bids.map((o) => ({ o, isBid: true })), ...st.asks.map((o) => ({ o, isBid: false }))].map(({ o, isBid }) => (
                  <tr key={`${isBid}-${o.id}`}>
                    <td className={isBid ? "ex-up" : "ex-down"}>{isBid ? "Buy" : "Sell"}</td>
                    <td>{o.owner}</td>
                    <td>{fromUnits(o.price, qp)}</td>
                    <td>{isBid ? `${fromUnits(o.remaining, qp)} ${Q}` : `${fromUnits(o.remaining, bp)} ${B}`}</td>
                    <td>
                      <button className="btn btn--small btn--outline" disabled={!canEdit}
                        onClick={() => run("refund the order", () => adminCancelMany(session!, m.id, [{ isBid, id: o.id }]), "Order refunded to its owner.")}>Refund</button>{" "}
                      <button className="btn btn--small btn--outline" disabled={!canEdit} title="Use only if refunding fails (owner rejects transfers)"
                        onClick={async () => {
                          const ok = await confirmAction({
                            title: "Force-close order",
                            message: `Only for an order whose owner can't receive a refund (e.g. a contract that rejects transfers and is blocking the book). The funds move to ${o.owner}'s claimable balance on the exchange — they can withdraw them any time with Claim. They never go anywhere else.`,
                            confirmLabel: "Force-close",
                            danger: true,
                          });
                          if (ok) run("force-close the order", () => forceClose(session!, m.id, isBid, o.id), "Order closed; funds are claimable by the owner.");
                        }}>Force-close</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
